/**
 * Sysadmin sign-in, which is a different door from the one staff use.
 *
 * Google rather than a magic link, for two reasons. A sysadmin account is the
 * one that can reach every hospital's data, so it should sit behind an
 * identity provider with its own multi-factor rather than behind whoever
 * controls an inbox. And it belongs to us rather than to a customer, so
 * federating it costs a customer nothing.
 *
 * The console is a separate host — console.standingdocs.com — because
 * cross-organization access should not be a role toggle inside a customer's
 * own tenant. That is presentation: the claim, and the backend's check of it,
 * are what actually decide.
 */

import {
  GoogleAuthProvider,
  signInWithPopup,
  type UserCredential,
} from 'firebase/auth';
import { useQuery } from '@tanstack/react-query';
import { auth } from '../../lib/firebase';
import { call } from '../../lib/callable';

/**
 * The provider, built lazily.
 *
 * Constructed on demand rather than at module load. The previous client shipped a
 * bug for months because it built its Google provider at startup, which threw
 * on every page load before anybody had tried to sign in.
 */
export function signInWithGoogle(): Promise<UserCredential> {
  const provider = new GoogleAuthProvider();
  // Always ask which account. A sysadmin often holds a personal Google
  // account in the same browser, and silently reusing it is how somebody
  // ends up staring at a permission error with no idea why.
  provider.setCustomParameters({ prompt: 'select_account' });
  return signInWithPopup(auth, provider);
}

export interface ConsoleOrg {
  orgId: string;
  name: string;
  status: string;
  appMode: string | null;
  primaryDomain: string | null;
  createdAt: unknown;
  remindersEnabled: boolean;
  /** Someone holds the organization's System Admin role (active). */
  hasSystemAdmin?: boolean;
  hasDomain?: boolean;
  /** Active, reminders on, System Admin named, domain registered. */
  live?: boolean;
}

export function useOrganizations(enabled: boolean) {
  return useQuery({
    queryKey: ['console-orgs'],
    enabled,
    retry: false,
    queryFn: () =>
      call<Record<string, never>, { organizations: ConsoleOrg[]; total: number }>(
        'sysAdminListOrganizations',
        {},
      ),
  });
}

/** Sort customers so the ones needing attention are not buried. */
export function sortOrganizations(orgs: readonly ConsoleOrg[]): ConsoleOrg[] {
  // Customers not yet live lead — something on their checklist is still
  // ours to do. Then the live ones, then suspended and the rest.
  const rank = (o: ConsoleOrg) =>
    o.status === 'active' ? (o.live === false ? 0 : 1) : 2;

  return [...orgs].sort(
    (a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name),
  );
}
